import React, { forwardRef } from 'react';
import ReactModal from 'react-modal';
import { css, cx } from '@emotion/css';
import { theme, IconConfirmation, IconWarning } from '@contrast-security-inc/design-system-foundations';
import { TextOverflow } from '../TextOverflow';
const closeTimeInSeconds = 0.3;
const openTimeInSeconds = 0.425;
const borderRadius = 8;
const getOverlayColor = (opacity) => `rgba(41, 41, 41, ${opacity})`;
const getStyledProps = (zIndex, className) => ({
    overlayClassName: {
        base: css `
      display: block;
      inset: 0px;
      overflow-y: auto;
      position: fixed;
      transition: background-color ${openTimeInSeconds}s linear;
      background-color: ${getOverlayColor(0.4)};
      z-index: ${zIndex};
    `,
        afterOpen: css `
      background-color: ${getOverlayColor(0.4)};
    `,
        beforeClose: css `
      transition: background-color ${closeTimeInSeconds}s linear;
      background-color: ${getOverlayColor(0)};
    `,
    },
    className: {
        base: cx(css `
      align-items: center;
      display: flex;
      justify-content: center;
      margin: 50px auto;
      min-height: calc(100vh - 100px);
      outline: none;
      pointer-events: none;
      position: relative;
      transition: transform ${openTimeInSeconds}s ease-out;
      transform: translate(0, -100vh);

      > * {
        margin-bottom: 150px;
      }
    `, className, 'ds-modal'),
        afterOpen: css `
      transform: translate(0, 0);
    `,
        beforeClose: css `
      transition: transform ${closeTimeInSeconds}s ease-out;
      transform: translate(0, -100vh);
    `,
    },
    closeTimeoutMS: closeTimeInSeconds * 1000,
});
const bodyCss = css `
  overflow: hidden;
`;
const containerCss = css `
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  background-color: ${theme.color.white};
  box-shadow: ${theme.shadow.md};
  border-radius: ${borderRadius}px;
  min-height: 242px;
`;
const sizeToClassName = {
    sm: cx(containerCss, css `
      width: 24%;
      min-width: 400px;
      max-width: 700px;
    `),
    md: cx(containerCss, css `
      width: 40%;
      min-width: 600px;
      max-width: 900px;
    `),
    lg: cx(containerCss, css `
      width: 60%;
      min-width: 760px;
      max-width: 1060px;
    `),
};
const headerContainerCss = css `
  gap: 8px;
  height: 60px;
  align-items: center;
  border-bottom: 1px solid ${theme.color.neutral40};
  border-radius: ${borderRadius}px ${borderRadius}px 0 0;
  padding: 0px 24px;
  display: flex;
  flex-direction: row;
`;
const headerTitleCss = css `
  flex: 1 1 auto;
  overflow: hidden;
  word-wrap: normal;
  white-space: nowrap;
  margin-top: 2.5px;
`;
const bodyContainerCss = css `
  padding: 32px 24px 40px;
`;
const footerContainerCss = css `
  display: flex;
  gap: 8px;
  justify-content: end;
  margin-left: auto;
  padding: 0px 24px 32px;
  margin-top: auto;
`;
const variantToHeaderClassName = {
    info: cx(headerContainerCss, css `
    background-color: ${theme.color.neutral20};
  `),
    confirmation: cx(headerContainerCss, css `
    background-color: ${theme.color.neutral100};
  `),
    warning: cx(headerContainerCss, css `
    background-color: ${theme.color.neutral100};
  `),
};
const variantToHeaderTitleColor = {
    info: 'neutral100',
    confirmation: 'white',
    warning: 'white',
};
export const ModalBody = ({ children }) => {
    return React.createElement("div", { className: bodyContainerCss }, children);
};
ModalBody.displayName = 'ModalBody';
export const ModalFooter = ({ children }) => {
    return React.createElement("div", { className: footerContainerCss }, children);
};
ModalFooter.displayName = 'ModalFooter';
export const Modal = forwardRef(({ title, isOpen = false, size, variant, children, onClose, testId, className, style, Icon, zIndex = theme.zIndex.modal, }, ref) => {
    let headerIcon;
    switch (variant) {
        case 'confirmation':
            headerIcon = React.createElement(IconConfirmation, { size: "md", color: "white", testId: `${testId}-title-icon` });
            break;
        case 'warning':
            headerIcon = React.createElement(IconWarning, { size: "md", color: "white", testId: `${testId}-title-icon` });
            break;
        case 'info':
        default:
            headerIcon = Icon && React.createElement(Icon, { size: "md", color: "neutral100", testId: `${testId}-title-icon` });
    }
    return (React.createElement(ReactModal, { ...getStyledProps(zIndex, className), ariaHideApp: false, bodyOpenClassName: bodyCss, contentRef: ref, isOpen: isOpen, onRequestClose: onClose, shouldCloseOnOverlayClick: false, style: {
            content: style,
        }, testId: testId },
        React.createElement("div", { className: sizeToClassName[size] },
            React.createElement("div", { className: variantToHeaderClassName[variant] },
                headerIcon,
                React.createElement(TextOverflow, { className: headerTitleCss, color: variantToHeaderTitleColor[variant], testId: `${testId}-title`, font: "titleH5" }, title)),
            isOpen && children)));
});
export function getI18nForPrimaryBtn(variant) {
    switch (variant) {
        case 'info':
            return 'okay';
        case 'confirmation':
            return 'save';
        case 'warning':
            return 'delete';
    }
}
Modal.displayName = 'Modal';
